import {
  Box,
  Button,
  Checkbox,
  CloseIcon,
  FormControlLabel,
  IconButton,
  Progress,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Link, Redirect } from "react-router-dom"
import {
  RoutingFlowLayoutBase,
  CloseButtonReactRouterLink
} from "#/components/carrot-plans/shared/RoutingFlowContainer"
import { useIsRtxParentCompany } from "#/components/consent/hooks/useIsRtxParentCompany"
import { useSubmitRtxConsent } from "#/components/consent/hooks/useSubmitRtxConsent"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { RtxConsentInnerContent, rtxConsentCheckboxLabel } from "#/components/consent/RtxConsentInnerContent"

export function RtxConsent() {
  const { isRtx, isLoading } = useIsRtxParentCompany()

  if (isLoading) {
    return (
      <RoutingFlowLayoutBase>
        <Progress />
      </RoutingFlowLayoutBase>
    )
  }

  if (!isRtx) {
    return <Redirect to="/" />
  }

  return (
    <RoutingFlowLayoutBase>
      <RtxConsentForm />
    </RoutingFlowLayoutBase>
  )
}

const RtxConsentForm = () => {
  const intl = useIntl()
  const [consentIsChecked, setConsentIsChecked] = React.useState(false)
  const [hasSubmitted, setHasSubmitted] = React.useState(false)
  const [hasError, setHasError] = React.useState(false)
  const { mutateAsync: submitRtxConsent, isLoading: isSubmitting } = useSubmitRtxConsent()

  async function handleConsentResponse(optIn: boolean): Promise<void> {
    try {
      await submitRtxConsent({ optIn })
      setHasSubmitted(true)
    } catch (error) {
      setHasError(true)
    }
  }

  if (hasSubmitted) {
    return <SuccessMessage />
  }

  if (hasError) {
    return (
      <>
        <CloseButtonWithWrapper />
        <ErrorPage />
      </>
    )
  }

  return (
    <>
      <CloseButtonWithWrapper />
      <Stack>
        <RtxConsentInnerContent />
        <FormControlLabel
          control={<Checkbox checked={consentIsChecked} onChange={(e) => setConsentIsChecked(e.target.checked)} />}
          label={intl.formatMessage(rtxConsentCheckboxLabel)}
        />
        <ConsentButtons
          handleConsentResponse={handleConsentResponse}
          isLoading={isSubmitting}
          consentIsChecked={consentIsChecked}
        />
      </Stack>
    </>
  )
}

const ConsentButtons = ({
  handleConsentResponse,
  isLoading,
  consentIsChecked
}: {
  handleConsentResponse: (optIn: boolean) => void
  isLoading: boolean
  consentIsChecked: boolean
}) => {
  const theme = useTheme()

  return (
    <Stack
      marginTop={theme.spacing(theme.tokens.spacing.lg)}
      spacing={theme.spacing(theme.tokens.spacing.sm)}
      sx={{ display: "inline-flex", flexDirection: "row-reverse" }}
    >
      <Button
        color="secondary"
        disabled={isLoading || !consentIsChecked}
        variant="contained"
        onClick={() => handleConsentResponse(true)}
      >
        <FormattedMessage defaultMessage="Submit" />
      </Button>
      <Button
        color="secondary"
        disabled={isLoading || consentIsChecked}
        variant="outlined"
        onClick={() => handleConsentResponse(false)}
      >
        <FormattedMessage defaultMessage="Decline" />
      </Button>
    </Stack>
  )
}

const SuccessMessage = () => {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} margin={theme.spacing(theme.tokens.spacing.lg)}>
      <Typography variant="h3" component="h2" color="inherit">
        <FormattedMessage defaultMessage="Your response has been recorded." />
      </Typography>
      <Box textAlign="end">
        <Button variant="contained" color="primary" component={Link} to="/">
          <FormattedMessage defaultMessage="Go to Carrot home" />
        </Button>
      </Box>
    </Stack>
  )
}

const CloseButtonWithWrapper = () => {
  const intl = useIntl()

  return (
    <Box display="flex" justifyContent="flex-end">
      <IconButton
        data-testid="close-button"
        aria-label={intl.formatMessage({ defaultMessage: "Close" })}
        LinkComponent={CloseButtonReactRouterLink}
        href="/"
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}
